import React, { useCallback, useEffect, useRef, useState } from "react";
import { message as antMessage, Modal, Dropdown } from "antd";
import { useSelector } from "react-redux";
import { Wrapper } from "./style";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { renderMarkdown } from "./lib/markdown";
import { streamAiChatMessage } from "./lib/streamChat";
import {
  fetchSessions,
  fetchSession,
  deleteSession,
  pinSession,
  fetchAiCreditQuota,
} from "./lib/sessionsApi";
import { PlusIcon, PinIcon, PinOffIcon, TrashIcon, KebabIcon, SendIcon } from "./lib/icons";

const DEFAULT_USER_AVATAR = "/assets/media/avatars/blank.png";

const quickAsks = [
  { text: "Net profit this month?", query: "What is my net profit this month?" },
  { text: "Which SKUs are losing money?", query: "Which SKUs are bleeding money?" },
  { text: "What's my current TACoS?", query: "What is my current TACoS?" },
  { text: "Return losses this month?", query: "How much am I losing to returns this month?" },
  { text: "Top profitable SKUs?", query: "What are my top 5 most profitable SKUs?" },
];

const strategicAsks = [
  { text: "Revenue grew, profit didn't?", query: "My revenue grew but profit didn't. Why?" },
  { text: "Scale vs. kill - which SKUs?", query: "Which SKUs should I scale and which should I kill?" },
  { text: "True margin per unit?", query: "What's my true net margin per unit after all Amazon fees and ad costs?" },
  { text: "Worst-performing campaigns?", query: "Which ad campaigns are spending the most but contributing least to profit?" },
  { text: "Where are my profit leaks?", query: "Where are my biggest profit leaks hiding?" },
];

function formatSessionTime(ts) {
  if (!ts) return "";
  const date = new Date(ts * 1000);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function BotAvatar() {
  return (
    <div className="av bot">
      <div className="bot-mark">
        <img
          src="/assets/media/sellerView-favicon.png"
          className="copilot-bot-avatar-img"
          alt=""
        />
      </div>
    </div>
  );
}

function MessageBubble({ message, userAvatarSrc }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="row user">
        <div className="av user">
          <img
            src={userAvatarSrc}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = DEFAULT_USER_AVATAR;
            }}
          />
        </div>
        <div className="bubble user">{message.content}</div>
      </div>
    );
  }

  const hasContent = Boolean(message.content?.trim());
  if (!hasContent && !message.toolLoading) return null;

  return (
    <div className="row">
      <BotAvatar />
      <div className="bubble bot">
        {hasContent ? (
          <div
            className={`md-content${message.streaming ? " streaming" : ""}`}
            dangerouslySetInnerHTML={{ __html: message.html || "" }}
          />
        ) : null}
        {message.toolLoading ? (
          <div className="tool-status">
            <span className="tool-dot" />
            Analyzing data...
          </div>
        ) : null}
        {message.streaming && hasContent ? <span className="stream-cursor" aria-hidden /> : null}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="row">
      <BotAvatar />
      <div className="bubble bot">
        <div className="typ">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

export default function AICopilot() {
  const { themeMode } = useThemeMode();
  const isDark = themeMode === "dark";
  const brandLogoSrc = isDark
    ? "/assets/media/SellerViewLight.png"
    : "/assets/media/SellerView.png";

  const userData = useSelector((state) => state.user.data);
  const userAvatarSrc =
    userData?.user?.user_data?.photo || userData?.user?.photo || DEFAULT_USER_AVATAR;
  const token = userData?.token;

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [awaitingFirstChunk, setAwaitingFirstChunk] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [pinningSessionId, setPinningSessionId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [quota, setQuota] = useState(null);

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const streamBufferRef = useRef("");
  const assistantIndexRef = useRef(-1);
  const currentSessionIdRef = useRef(null);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, awaitingFirstChunk, isStreaming]);

  const loadSessions = useCallback(async () => {
    if (!token) return;
    setSessionsLoading(true);
    try {
      const records = await fetchSessions(token);
      setSessions(records);
    } catch (err) {
      if (err?.message !== "Unauthorized") {
        antMessage.error(err?.message || "Failed to load sessions");
      }
    } finally {
      setSessionsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const loadQuota = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchAiCreditQuota(token);
      if (data) setQuota(data);
    } catch (err) {
      if (err?.message !== "Unauthorized") {
        // silent: avoid noisy toasts for quota polling
        // eslint-disable-next-line no-console
        console.warn("Failed to load AI credit quota", err);
      }
    }
  }, [token]);

  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const isUnlimited = !!quota?.is_unlimited;
  const creditsUsed = Number(quota?.ai_credits_used ?? 0) || 0;
  const creditsLimit = Number(quota?.ai_credit_limit ?? 0) || 0;
  const creditsRemaining =
    quota?.ai_credits_remaining === null || quota?.ai_credits_remaining === undefined
      ? null
      : Number(quota.ai_credits_remaining) || 0;
  const quotaExceeded =
    !!quota && !isUnlimited && creditsRemaining !== null && creditsRemaining <= 0;
  const quotaLabel = quota
    ? isUnlimited
      ? `${creditsUsed} Credits Used`
      : `${creditsUsed}/${creditsLimit} Credits Used`
    : null;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991) setIsSidebarOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsSidebarOpen(false);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const adjustTextarea = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 110)}px`;
  };

  const updateAssistant = useCallback((patch, meta = {}) => {
    setMessages((prev) => {
      const next = [...prev];
      let idx = assistantIndexRef.current;

      if (idx < 0 || next[idx]?.role !== "assistant") {
        idx = next.length;
        assistantIndexRef.current = idx;
        next.push({
          role: "assistant",
          content: "",
          html: "",
          streaming: true,
          toolLoading: false,
          ...meta,
        });
      }

      const current = next[idx];
      const content = patch.content !== undefined ? patch.content : current.content;
      const shouldRender = patch.content !== undefined || patch.finalize;

      next[idx] = {
        ...current,
        ...patch,
        content,
        html: shouldRender ? renderMarkdown(content) : current.html,
      };

      return next;
    });
  }, []);

  const appendToAssistant = useCallback(
    (text) => {
      streamBufferRef.current += text;
      const content = streamBufferRef.current;
      setAwaitingFirstChunk(false);
      updateAssistant({
        content,
        streaming: true,
        toolLoading: false,
      });
    },
    [updateAssistant]
  );

  const streamEventRef = useRef(null);

  const handleStreamEvent = useCallback(
    (event) => {
      switch (event.type) {
        case "meta":
          streamBufferRef.current = "";
          if (event.session_id) {
            const wasNew = !currentSessionIdRef.current;
            currentSessionIdRef.current = event.session_id;
            setCurrentSessionId(event.session_id);
            if (wasNew) {
              loadSessions();
            }
          }
          updateAssistant({}, {
            sessionId: event.session_id,
            model: event.model,
            intent: event.intent,
          });
          break;
        case "tool_calls":
          setAwaitingFirstChunk(false);
          updateAssistant({ toolLoading: true });
          break;
        case "text_delta":
          if (event.text) appendToAssistant(event.text);
          break;
        case "done":
          updateAssistant({
            content: streamBufferRef.current,
            finalize: true,
            streaming: false,
            toolLoading: false,
          });
          setIsStreaming(false);
          setAwaitingFirstChunk(false);
          abortRef.current = null;
          loadSessions();
          loadQuota();
          break;
        default:
          break;
      }
    },
    [appendToAssistant, updateAssistant, loadSessions, loadQuota]
  );

  streamEventRef.current = handleStreamEvent;

  const handleSend = async (override) => {
    const query = (override ?? inputValue).trim();
    if (!query || isStreaming) return;

    if (!token) {
      antMessage.error("Please sign in to use Copilot.");
      return;
    }

    if (quotaExceeded) {
      antMessage.error(
        "You've reached your AI credit limit for this period. Please upgrade your plan to continue."
      );
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setInputValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    streamBufferRef.current = "";
    assistantIndexRef.current = -1;

    setMessages((prev) => [...prev, { role: "user", content: query }]);
    setIsStreaming(true);
    setAwaitingFirstChunk(true);

    try {
      await streamAiChatMessage({
        message: query,
        token,
        sessionId: currentSessionIdRef.current || undefined,
        signal: controller.signal,
        onEvent: (event) => streamEventRef.current?.(event),
      });

      if (assistantIndexRef.current < 0 && streamBufferRef.current) {
        setMessages((prev) => {
          const next = [
            ...prev,
            {
              role: "assistant",
              content: streamBufferRef.current,
              html: renderMarkdown(streamBufferRef.current),
              streaming: false,
            },
          ];
          assistantIndexRef.current = next.length - 1;
          return next;
        });
      } else if (assistantIndexRef.current >= 0) {
        updateAssistant({
          content: streamBufferRef.current,
          finalize: true,
          streaming: false,
          toolLoading: false,
        });
      }
    } catch (err) {
      if (err?.name === "AbortError") return;

      const errText =
        err?.message || "Something went wrong while reaching Copilot. Please try again.";

      if (assistantIndexRef.current >= 0) {
        updateAssistant({
          content: streamBufferRef.current || errText,
          finalize: true,
          streaming: false,
          toolLoading: false,
        });
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: errText,
            html: renderMarkdown(errText),
            streaming: false,
          },
        ]);
      }
      antMessage.error(errText);
      loadQuota();
    } finally {
      setIsStreaming(false);
      setAwaitingFirstChunk(false);
      abortRef.current = null;
    }
  };

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleNewChat = () => {
    if (isStreaming) abortRef.current?.abort();
    setMessages([]);
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    streamBufferRef.current = "";
    assistantIndexRef.current = -1;
    setIsStreaming(false);
    setAwaitingFirstChunk(false);
    setInputValue("");
    setIsSidebarOpen(false);
  };

  const handleSelectSession = async (sessionId) => {
    if (sessionId === currentSessionId || loadingSessionId) {
      setIsSidebarOpen(false);
      return;
    }
    if (isStreaming) abortRef.current?.abort();

    setLoadingSessionId(sessionId);
    try {
      const data = await fetchSession(sessionId, token);
      const loadedMessages = (data?.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        html: m.role === "assistant" ? renderMarkdown(m.content || "") : undefined,
        streaming: false,
      }));
      setMessages(loadedMessages);
      setCurrentSessionId(sessionId);
      currentSessionIdRef.current = sessionId;
      streamBufferRef.current = "";
      assistantIndexRef.current = -1;
      setIsStreaming(false);
      setAwaitingFirstChunk(false);
      setIsSidebarOpen(false);
    } catch (err) {
      antMessage.error(err?.message || "Failed to load session");
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handleTogglePin = async (sessionId, currentlyPinned) => {
    if (pinningSessionId) return;
    setPinningSessionId(sessionId);
    try {
      const updated = await pinSession(sessionId, !currentlyPinned, token);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                is_pinned: updated?.is_pinned ?? !currentlyPinned,
                pinned_at: updated?.pinned_at ?? (!currentlyPinned ? Math.floor(Date.now() / 1000) : null),
              }
            : s
        )
      );
    } catch (err) {
      antMessage.error(err?.message || "Failed to update pin");
    } finally {
      setPinningSessionId(null);
    }
  };

  const { pinnedSessions, unpinnedSessions } = React.useMemo(() => {
    const pinned = [];
    const unpinned = [];
    sessions.forEach((s) => {
      if (s.is_pinned) pinned.push(s);
      else unpinned.push(s);
    });
    const byPinnedAt = (a, b) => (b.pinned_at || 0) - (a.pinned_at || 0);
    const byUpdated = (a, b) =>
      (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    pinned.sort(byPinnedAt);
    unpinned.sort(byUpdated);
    return { pinnedSessions: pinned, unpinnedSessions: unpinned };
  }, [sessions]);

  const renderSessionItem = (s) => (
    <div
      key={s.id}
      className={`qq session-item${currentSessionId === s.id ? " active" : ""}${loadingSessionId === s.id ? " loading" : ""}${s.is_pinned ? " pinned" : ""}`}
      onClick={() => handleSelectSession(s.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSelectSession(s.id);
      }}
    >
      <div className="session-info">
        <div className="session-title" title={s.title}>
          {s.is_pinned ? <PinIcon className="pin-indicator" size={12} /> : null}
          <span>{s.title || "Untitled chat"}</span>
        </div>
        <div className="session-time">{formatSessionTime(s.updated_at || s.created_at)}</div>
      </div>
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        overlayClassName="session-menu-overlay"
        menu={{
          items: [
            {
              key: "pin",
              label: s.is_pinned ? "Unpin chat" : "Pin chat",
              disabled: pinningSessionId === s.id,
              icon: s.is_pinned ? <PinOffIcon /> : <PinIcon />,
            },
            { type: "divider" },
            {
              key: "delete",
              label: "Delete chat",
              danger: true,
              disabled: deletingSessionId === s.id,
              icon: <TrashIcon />,
            },
          ],
          onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            if (key === "pin") handleTogglePin(s.id, !!s.is_pinned);
            else if (key === "delete") handleDeleteSession(s.id, s.title);
          },
        }}
      >
        <button
          type="button"
          className="session-action session-kebab"
          title="More options"
          onClick={(e) => e.stopPropagation()}
        >
          <KebabIcon />
        </button>
      </Dropdown>
    </div>
  );

  const handleDeleteSession = (sessionId, sessionTitle) => {
    Modal.confirm({
      className: "copilot-delete-session-modal",
      title: <span className="copilot-delete-modal-title">Delete this chat?</span>,
      content: (
        <span className="copilot-delete-modal-body">
          {sessionTitle ? `"${sessionTitle}" will be permanently removed.` : "This chat will be permanently removed."}
        </span>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setDeletingSessionId(sessionId);
        try {
          await deleteSession(sessionId, token);
          setSessions((prev) => prev.filter((s) => s.id !== sessionId));
          if (currentSessionIdRef.current === sessionId) {
            handleNewChat();
          }
          antMessage.success("Chat deleted");
        } catch (err) {
          antMessage.error(err?.message || "Failed to delete chat");
        } finally {
          setDeletingSessionId(null);
        }
      },
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const showWelcome = messages.length === 0 && !isStreaming;
  const showTyping = awaitingFirstChunk && isStreaming;
  const handlePromptClick = (query) => {
    setIsSidebarOpen(false);
    handleSend(query);
  };

  return (
    <Wrapper style={{ marginTop: 30 }} className={`ai-copilot-page${isSidebarOpen ? " sidebar-open" : ""}`}>
      <header>
        <button
          type="button"
          className="mobile-menu-btn"
          aria-label="Open chat menu"
          aria-expanded={isSidebarOpen}
          onClick={() => setIsSidebarOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="logo-wrap">
          <div className="logo-square">
            <img src="/assets/media/sellerView-favicon.png" alt="Sellerview Logo" />
          </div>
          <div className="logo-text">
            <img src={brandLogoSrc} alt="AI Bot" className="copilot-logo-img copilot-logo-img--header" /> <span> Copilot</span>
          </div>
        </div>
        <div className="hdr-right">
          <div className="badge copilot-beta-badge">BETA</div>
          {quotaLabel ? (
            <div
              className={`badge credits-badge${quotaExceeded ? " exceeded" : ""}`}
              title={
                quotaExceeded
                  ? "AI credit limit reached"
                  : "AI credits usage for the current period"
              }
            >
              {quotaLabel}
            </div>
          ) : null}
          {/* <div className="status">
            <div className="dot" />
            Online
          </div> */}
        </div>
      </header>

      <div className="chat-wrap">
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close chat menu"
          onClick={() => setIsSidebarOpen(false)}
        />
        <aside className="sidebar">
          <div className="mobile-sidebar-head">
            <div className="mobile-sidebar-title">Copilot Menu</div>
            <button
              type="button"
              className="mobile-sidebar-close"
              aria-label="Close chat menu"
              onClick={() => setIsSidebarOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="sidebar-sticky">
            <button
              type="button"
              className="new-chat-btn"
              onClick={handleNewChat}
              disabled={isStreaming}
            >
              <PlusIcon />
              <span>New Chat</span>
            </button>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-scroll">
            <div className="s-label">Quick Asks</div>
            {quickAsks.map((item) => (
              <button key={item.text} type="button" className="qq" onClick={() => handlePromptClick(item.query)} disabled={isStreaming}>
                <span>{item.text}</span>
              </button>
            ))}

            <div className="s-divider" />

            <div className="s-label">Strategic</div>
            {strategicAsks.map((item) => (
              <button key={item.text} type="button" className="qq nv" onClick={() => handlePromptClick(item.query)} disabled={isStreaming}>
                <span>{item.text}</span>
              </button>
            ))}

            <div className="s-divider" />

            <div className="s-label-row">
              <div className="s-label">Chat History</div>
              {sessions.length > 0 ? (
                <span className="sessions-count">{sessions.length}</span>
              ) : null}
            </div>
            {sessionsLoading && sessions.length === 0 ? (
              <div className="sessions-empty">Loading...</div>
            ) : pinnedSessions.length === 0 && unpinnedSessions.length === 0 ? (
              <div className="sessions-empty">No past chats yet</div>
            ) : (
              <div className="sessions-list">
                {pinnedSessions.length > 0 ? (
                  <div className="pinned-sessions-sticky">{pinnedSessions.map(renderSessionItem)}</div>
                ) : null}
                {unpinnedSessions.map(renderSessionItem)}
              </div>
            )}
            </div>
          </div>
        </aside>

        <div className="chat-main">
          <div className="messages">
            {showWelcome ? (
              <div className="welcome">
                <div className="w-icon">
                  <div className="bot-mark">
                    <img
                      src="/assets/media/sellerView-favicon.png"
                      alt="AI Bot"
                      className="copilot-bot-avatar-img copilot-bot-avatar-img--welcome"
                    />
                  </div>
                </div>
                <h2>
                  <img src={brandLogoSrc} alt="AI Bot" className="copilot-logo-img copilot-logo-img--welcome" /> <span>Copilot</span>
                </h2>
                <p>
                  Ask me anything about your Amazon business - margins, TACoS, ad spend, returns, and which SKUs are
                  actually making you money.
                </p>
                <div className="chips">
                  <button type="button" className="chip" onClick={() => handleSend("What is my net profit this month?")} disabled={isStreaming}>
                    Net profit
                  </button>
                  <button type="button" className="chip" onClick={() => handleSend("What is my current TACoS?")} disabled={isStreaming}>
                    TACoS
                  </button>
                  <button type="button" className="chip" onClick={() => handleSend("Where are my biggest profit leaks?")} disabled={isStreaming}>
                    Profit leaks
                  </button>
                  <button
                    type="button"
                    className="chip"
                    onClick={() => handleSend("Which SKUs should I scale and which should I kill?")}
                    disabled={isStreaming}
                  >
                    SKU strategy
                  </button>
                </div>
              </div>
            ) : null}

            {messages.map((msg, idx) => (
              <MessageBubble
                key={`${msg.role}-${idx}-${msg.content?.slice(0, 24)}`}
                message={msg}
                userAvatarSrc={userAvatarSrc}
              />
            ))}
            {showTyping ? <TypingIndicator /> : null}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-bar">
            {quotaExceeded ? (
              <div className="quota-banner" role="alert">
                You've used all {creditsLimit} AI credits for this period.
                Please upgrade your plan to continue chatting with Copilot.
              </div>
            ) : null}
            <div className="irow">
              <textarea
                ref={textareaRef}
                rows={1}
                value={inputValue}
                placeholder={
                  quotaExceeded
                    ? "AI credit limit reached. Upgrade to continue..."
                    : "Ask about your margins, TACoS, SKUs, returns..."
                }
                onKeyDown={handleKeyDown}
                disabled={isStreaming || quotaExceeded}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  adjustTextarea();
                }}
              />
              <button
                type="button"
                className="sbtn"
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isStreaming || quotaExceeded}
              >
                <SendIcon />
              </button>
            </div>
            <div className="hint">
              <strong>sellerview.AI Copilot</strong> can make mistakes. Please double-check responses.
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
